import { useRef, useEffect, useCallback } from 'react'
import { usePostHogEvent, DivinationChannel } from '../../PostHog/index'

interface DivinationDocumentEvent {
  type: 'oracle' | 'tarot'
  readingId?: string | null
}

/**
 * Tracks PostHog analytics events for divination features (tarot + oracle cards).
 *
 * Handles:
 * - Tarot / oracle document arrival → `ai_enriched_document_received`
 * - Chat experience lifecycle → `ai_experience_entered` / `ai_experience_exited`
 *
 * @param channel - The current divination channel (drives the chat
 *   experience identity via the surrounding ChatSessionContextProvider;
 *   used here only to gate exit-on-unmount tracking).
 */
export const useDivinationTracking = (channel?: DivinationChannel) => {
  const trackExperienceEntered = usePostHogEvent('ai_experience_entered')
  const trackExperienceExited = usePostHogEvent('ai_experience_exited')
  const trackEnrichedDocumentReceived = usePostHogEvent('ai_enriched_document_received')

  const lastReadingIdRef = useRef<string | null>(null)
  const channelRef = useRef(channel)
  channelRef.current = channel

  // Document arrival fires the unified ai_enriched_document_received event.
  // The hook still tracks readingId to make the new-vs-clarifying distinction
  // available for callers, but does not emit the legacy ai_tarot_* events.
  const handleDivinationDocument = useCallback((data: DivinationDocumentEvent) => {
    if (data.type === 'tarot') {
      if (data.readingId && data.readingId !== lastReadingIdRef.current) {
        lastReadingIdRef.current = data.readingId
      }
      trackEnrichedDocumentReceived('tarot', [], 'Tarot Card')
    } else if (data.type === 'oracle') {
      trackEnrichedDocumentReceived('oracleBegin', [], 'Card')
    }
  }, [trackEnrichedDocumentReceived])

  const handleChannelJoin = useCallback(() => {
    trackExperienceEntered()
  }, [trackExperienceEntered])

  // Fire experience-exited on unmount when we were tracking an experience
  useEffect(() => () => {
    if (channelRef.current) {
      trackExperienceExited()
    }
  }, [trackExperienceExited])

  return { handleDivinationDocument, handleChannelJoin }
}

export type { DivinationDocumentEvent }
